import { useState, useEffect } from 'react';
import { ChannelMemberIndex } from '../types';
import { channelService } from '../services/channelService';

/**
 * 대화방 검색용 참여자 인덱스 공유 캐시 (모듈 스코프).
 *
 * 채널 목록 응답에는 멤버 이름이 없다(memberCount 숫자뿐). 그래서 "김철수가 있는 방"을
 * 찾으려면 별도 데이터가 필요한데, 이걸 getChannelListForUser에 얹으면 접속마다 도는
 * 상시 경로(이미 채널당 상관 서브쿼리 7개)에 검색 전용 비용을 물리게 된다.
 * 대신 검색창을 처음 열 때 1회만 받아 캐시하고, 이후 타이핑은 전부 로컬 필터로 처리한다.
 *
 * 캐시 구조는 useAffiliateUserSearch의 직원 명부 캐시와 같다 — in-flight promise까지
 * 공유해 대화방 목록·전달 모달이 동시에 열려도 요청은 1건이다.
 */
let memberIndexCache: ChannelMemberIndex | null = null;
let memberIndexInflight: Promise<ChannelMemberIndex> | null = null;

const EMPTY_INDEX: ChannelMemberIndex = {};

function fetchMemberIndex(): Promise<ChannelMemberIndex> {
  if (memberIndexCache) return Promise.resolve(memberIndexCache);
  if (memberIndexInflight) return memberIndexInflight;

  const promise = channelService
    .getMemberIndex()
    .then((index) => {
      memberIndexCache = index || EMPTY_INDEX;
      return memberIndexCache;
    })
    .catch((err) => {
      // fail-open: 서버가 구버전(404)이거나 요청이 실패해도 이름·설명 검색은 그대로 동작해야 한다.
      // 사용자가 요청한 적 없는 배경 fetch라 토스트도 띄우지 않는다. 캐시에 담지 않으므로
      // 다음에 검색창을 열면 다시 시도한다.
      console.warn('[ChannelMemberIndex] 참여자 인덱스를 불러오지 못했습니다 (이름·설명 검색은 정상)', err);
      return EMPTY_INDEX;
    })
    .finally(() => {
      memberIndexInflight = null;
    });

  memberIndexInflight = promise;
  return promise;
}

/** 멤버 변동(초대/퇴장/새 채널) 시 호출 — 다음 검색창 오픈에 다시 받는다 */
export function invalidateChannelMemberIndex() {
  memberIndexCache = null;
}

/** 로그아웃 시 다른 계정의 참여자 정보가 남지 않도록 비운다 */
export function clearChannelMemberIndex() {
  memberIndexCache = null;
  memberIndexInflight = null;
}

/**
 * 검색창이 열려 있을 때만(isActive) 참여자 인덱스를 로드하는 lazy 훅.
 *
 * 알려진 한계: 검색창을 열어 둔 채로 멤버가 바뀌면 그 세션에서는 반영되지 않고,
 * 닫았다 다시 열면 갱신된다. 검색 정확도에만 영향이 있어 이 정도로 둔다.
 */
export function useChannelMemberIndex(isActive: boolean): ChannelMemberIndex {
  const [index, setIndex] = useState<ChannelMemberIndex>(() => memberIndexCache || EMPTY_INDEX);

  useEffect(() => {
    if (!isActive) return;
    // 캐시가 살아 있으면 즉시 반영 (invalidate 후 재오픈이면 null이라 아래 fetch로 간다)
    if (memberIndexCache) {
      setIndex(memberIndexCache);
      return;
    }

    let cancelled = false;
    fetchMemberIndex().then((loaded) => {
      if (!cancelled) setIndex(loaded);
    });
    return () => { cancelled = true; };
  }, [isActive]);

  return index;
}
